import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel.jsx';
import axiosInstance from '../../config/axiosApi/axiosApi.jsx';
import APIs from '../../Constants/apis.js';
import Preloader from '../../components/CustomComponents/preLoad/preLoader.js';
import showErrorToast from '../../components/CustomComponents/toast/toast.js';
import { logout } from '../../app/auth/authSlice.js';
import './revenue.scss';
import { REVENUE_COMPANY_FILTERS } from './revenueConstants.js';

const field = (obj, key) => obj?.[key] ?? obj?.[key.charAt(0).toUpperCase() + key.slice(1)];

const formatMoney = (amount, currency = 'USD') => {
  const value = parseFloat(amount) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency || ''} ${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  }
};

function Revenue() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [data, setData] = useState({
    revenueByCurrency: [],
    packageUsage: [],
    packageRunningCount: 0,
    packageExpiredCount: 0,
    noPackageRunningCount: 0,
    noPackageExpiredCount: 0,
  });

  const fetchRevenue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_REVENUE_OVERVIEW);
      if (response.data.status && response.data.data) {
        const d = response.data.data;
        const revenueByCurrency = Array.isArray(d.revenueByCurrency) ? d.revenueByCurrency : [];
        setData({
          revenueByCurrency,
          packageUsage: Array.isArray(d.packageUsage) ? d.packageUsage : [],
          packageRunningCount: field(d, 'packageRunningCount') || 0,
          packageExpiredCount: field(d, 'packageExpiredCount') || 0,
          noPackageRunningCount: field(d, 'noPackageRunningCount') || 0,
          noPackageExpiredCount: field(d, 'noPackageExpiredCount') || 0,
        });
        if (revenueByCurrency.length > 0) {
          const first = field(revenueByCurrency[0], 'currency') || 'USD';
          setSelectedCurrency((prev) =>
            prev && revenueByCurrency.some((r) => field(r, 'currency') === prev) ? prev : first
          );
        }
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate('/user/login', { replace: true }), 1000);
      } else {
        showErrorToast('Failed to load revenue overview');
      }
    }
  }, [dispatch, navigate]);

  useEffect(() => {
    fetchRevenue();
  }, [fetchRevenue]);

  const selectedRevenue = useMemo(() => {
    if (!data.revenueByCurrency.length) return null;
    return (
      data.revenueByCurrency.find((r) => field(r, 'currency') === selectedCurrency) ||
      data.revenueByCurrency[0]
    );
  }, [data.revenueByCurrency, selectedCurrency]);

  const countForFilter = (key) => {
    switch (key) {
      case 'package_running':
        return data.packageRunningCount;
      case 'package_expired':
        return data.packageExpiredCount;
      case 'no_package_running':
        return data.noPackageRunningCount;
      case 'no_package_expired':
        return data.noPackageExpiredCount;
      default:
        return 0;
    }
  };

  const RevenueMetricCard = ({ label, value, color }) => (
    <div className="col-xl-3 col-md-6">
      <div className="revenue-metric-card" style={{ borderLeftColor: color }}>
        <div className="revenue-metric-label">{label}</div>
        <div className="revenue-metric-value" style={{ color }}>{value}</div>
      </div>
    </div>
  );

  const CompanyStatCard = ({ filterKey }) => {
    const meta = REVENUE_COMPANY_FILTERS[filterKey];
    const count = countForFilter(filterKey);
    return (
      <div className="col-xl-3 col-md-6 mb-3">
        <Link
          to={`/revenue/companies?filter=${filterKey}`}
          className="revenue-stat-card text-decoration-none d-block h-100"
          style={{ borderLeftColor: meta.color }}
        >
          <div className="d-flex align-items-start gap-3">
            <div className="revenue-stat-icon" style={{ backgroundColor: `${meta.color}18`, color: meta.color }}>
              <i className={`fa ${meta.icon}`} />
            </div>
            <div className="flex-grow-1">
              <div className="revenue-stat-count" style={{ color: meta.color }}>{count}</div>
              <div className="revenue-stat-title">{meta.title}</div>
              <div className="revenue-stat-desc">{meta.description}</div>
              <span className="revenue-stat-link">View companies <i className="fa fa-arrow-right ms-1" /></span>
            </div>
          </div>
        </Link>
      </div>
    );
  };

  return (
    <div className="revenue-page">
      {loading && <Preloader />}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/dashboard">Home</Link></li>
        <li className="breadcrumb-item active">Revenue</li>
      </ol>
      <h1 className="page-header">
        Revenue <small>Recurring metrics & package adoption</small>
      </h1>

      <Panel>
        <PanelHeader>
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 w-100">
            <span>Recurring revenue (booked run-rate)</span>
            {data.revenueByCurrency.length > 1 && (
              <select
                className="form-select form-select-sm"
                style={{ width: 'auto', minWidth: '120px' }}
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
              >
                {data.revenueByCurrency.map((r) => (
                  <option key={field(r, 'currency')} value={field(r, 'currency')}>
                    {field(r, 'currency')}
                  </option>
                ))}
              </select>
            )}
          </div>
        </PanelHeader>
        <PanelBody>
          {selectedRevenue ? (
            <>
              <p className="text-muted small mb-3">
                Based on active package subscriptions and agreement months.{' '}
                <strong>{field(selectedRevenue, 'payingCompanyCount')}</strong> paying companies on a package.
              </p>
              <div className="row">
                <RevenueMetricCard
                  label="MRR (Monthly)"
                  value={formatMoney(field(selectedRevenue, 'mrr'), field(selectedRevenue, 'currency'))}
                  color="#348fe2"
                />
                <RevenueMetricCard
                  label="QRR (Quarterly)"
                  value={formatMoney(field(selectedRevenue, 'qrr'), field(selectedRevenue, 'currency'))}
                  color="#00acac"
                />
                <RevenueMetricCard
                  label="SRR (Semi-annual)"
                  value={formatMoney(field(selectedRevenue, 'srr'), field(selectedRevenue, 'currency'))}
                  color="#f59c1a"
                />
                <RevenueMetricCard
                  label="ARR (Annual)"
                  value={formatMoney(field(selectedRevenue, 'arr'), field(selectedRevenue, 'currency'))}
                  color="#ff5b57"
                />
              </div>
            </>
          ) : (
            <div className="alert alert-info mb-0">
              No active package subscriptions yet. Assign packages to companies to see recurring revenue.
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader>Restaurants by package & access status</PanelHeader>
        <PanelBody>
          <p className="text-muted small mb-3">
            Click a card to view the full company list for that segment (parent companies only).
          </p>
          <div className="row">
            {Object.keys(REVENUE_COMPANY_FILTERS).map((key) => (
              <CompanyStatCard key={key} filterKey={key} />
            ))}
          </div>
        </PanelBody>
      </Panel>

      {data.packageUsage.length > 0 && (
        <Panel>
          <PanelHeader>Package usage breakdown</PanelHeader>
          <PanelBody>
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Package</th>
                    <th className="text-center">Companies</th>
                    <th className="text-center">Paying</th>
                    <th className="text-center">Expired</th>
                    <th className="text-end">MRR</th>
                    <th className="text-end">ARR</th>
                  </tr>
                </thead>
                <tbody>
                  {data.packageUsage.map((row) => {
                    const cur = field(row, 'currency') || 'USD';
                    return (
                      <tr key={`${field(row, 'packageID')}-${cur}`}>
                        <td>
                          <strong>{field(row, 'packageName')}</strong>
                          <span className="badge bg-light text-dark ms-2">{cur}</span>
                        </td>
                        <td className="text-center">{field(row, 'totalCompanies')}</td>
                        <td className="text-center text-success fw-semibold">{field(row, 'payingCompanies')}</td>
                        <td className="text-center text-warning">{field(row, 'expiredCompanies')}</td>
                        <td className="text-end">{formatMoney(field(row, 'mrr'), cur)}</td>
                        <td className="text-end">{formatMoney(field(row, 'arr'), cur)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </PanelBody>
        </Panel>
      )}
    </div>
  );
}

export default Revenue;
